import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Input from "@components/Form/Input/Input";
import { useAddressAutocomplete } from "@hooks/useAddressAutocomplete";

const AddressAutocompleteInput = ({
  value,
  onChange,
  onAddressSelect,
  className,
  innerClassName,
  error,
  messagePosition,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // displayValue drives what the input shows — synced from value prop (form resets, external fills)
  const [displayValue, setDisplayValue] = useState(value ?? "");
  // searchQuery drives the API — only updated when the user is actively typing
  const [searchQuery, setSearchQuery] = useState("");

  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  const { suggestions, isLoading } = useAddressAutocomplete(searchQuery);

  // Sync display when parent updates the value externally (edit mode pre-fill, form reset, address select fill)
  // Deliberately does NOT update searchQuery so no API call fires.
  useEffect(() => {
    setDisplayValue(value ?? "");
  }, [value]);

  // Open dropdown when suggestions arrive
  useEffect(() => {
    if (suggestions.length > 0) {
      setOpen(true);
      setActiveIndex(-1);
    } else {
      setOpen(false);
    }
  }, [suggestions]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (val) => {
    setDisplayValue(val);
    setSearchQuery(val);   // user is typing → enable search
    onChange(val);
    if (!val) {
      setSearchQuery("");
      setOpen(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setDisplayValue(suggestion.streetAddress);
    setSearchQuery("");    // clear search → no follow-up API call
    setOpen(false);
    setActiveIndex(-1);
    onAddressSelect(suggestion);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(activeIndex + 1, suggestions.length - 1);
      setActiveIndex(next);
      scrollItemIntoView(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(activeIndex - 1, 0);
      setActiveIndex(prev);
      scrollItemIntoView(prev);
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const scrollItemIntoView = (index) => {
    const list = listRef.current;
    if (!list) return;
    list.children[index]?.scrollIntoView({ block: "nearest" });
  };

  return (
    <div ref={wrapperRef} className={clsx("relative", className)}>
      <Input
        value={displayValue}
        onChange={handleChange}
        placeholder={t("contact.streetAddressPlaceholder")}
        error={error}
        messagePosition={messagePosition}
        innerClassName={innerClassName}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {open && suggestions.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-silver-light rounded-xl shadow-lg max-h-52 overflow-y-auto"
          role="listbox"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(suggestion);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={clsx(
                "px-4 py-2.5 text-sm cursor-pointer transition-colors duration-100",
                index === activeIndex
                  ? "bg-primary bg-opacity-10 text-primary"
                  : "text-dawn-gray hover:bg-silver-light",
                index !== 0 && "border-t border-silver-light",
              )}
            >
              {suggestion.label}
            </li>
          ))}
        </ul>
      )}

      {isLoading && searchQuery.length >= 2 && !open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-silver-light rounded-xl shadow-sm px-4 py-2.5">
          <span className="text-xs text-cloudy-gray font-light-poppins">
            {t("contact.addressSearching")}
          </span>
        </div>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;
